import { baseFromDoc, type AppBase, type FirestoreBaseDoc, makeDocConverter } from '../base/firestore-model-base'

export type InvoiceItemType =
  | 'service_basic_fee'
  | 'postcard_postage_fee'
  | 'postcard_postage_discount'

/**
 * 利用明細（1行）のモデル（Project 配下のサブコレクションに保存）
 * - システム利用料: name + amount のみ
 * - はがき郵送費: name + unit_price + quantity + total_amount
 * - はがき不達控除: name + unit_price + quantity + total_amount
 *
 * created_at は FirestoreBaseDoc の created_at を利用する。
 */

/** Firestoreに保存される形（Timestampのまま / snake_case） */
export type InvoiceItemDoc = FirestoreBaseDoc & {
  /** 明細種別 */
  item_type?: InvoiceItemType
  /** 明細名称（例: システム利用料, はがき郵送費） */
  name: string
  /** 固定金額（システム利用料など） */
  amount?: number
  /** 単価（はがき郵送費など） */
  unit_price?: number
  /** 数量（枚数など） */
  quantity?: number
  /** 合計料金（単価×数量 など） */
  total_amount?: number
  /** 紐づく郵送回（はがき郵送費・控除のみ付与。郵送取消時にこの回の明細を特定する） */
  send_request_number?: string | null
}

/** アプリで使う形（Dateに変換 / camelCase） */
export type InvoiceItemEntity = AppBase & {
  itemType?: InvoiceItemType
  name: string
  amount?: number
  unitPrice?: number
  quantity?: number
  totalAmount?: number
  sendRequestNumber?: string | null
}

export const invoiceItemDocConverter = makeDocConverter<InvoiceItemDoc>()

export function toInvoiceItemEntity(id: string, docData: InvoiceItemDoc, path: string): InvoiceItemEntity {
  const base = baseFromDoc(id, docData, path)
  return {
    ...base,
    itemType: docData.item_type,
    name: docData.name,
    amount: docData.amount,
    unitPrice: docData.unit_price,
    quantity: docData.quantity,
    totalAmount: docData.total_amount,
    sendRequestNumber: docData.send_request_number ?? undefined,
  }
}

/** システム利用料の初期明細（プロジェクト作成時に保存） */
export const SERVICE_BASIC_ITEM_NAME = 'システム利用料'
export const POSTCARD_ITEM_NAME = 'はがき郵送費'
export const POSTCARD_DISCOUNT_ITEM_NAME = 'はがき不達控除（5%）'

export const INITIAL_INVOICE_ITEM_FIELDS: Pick<InvoiceItemDoc, 'item_type' | 'name' | 'amount'> = {
  item_type: 'service_basic_fee',
  name: 'システム利用料',
  amount: 66000,
}

/** はがき郵送費の単価・値引き率（固定） */
export const POSTCARD_UNIT_PRICE = 220
export const POSTCARD_DISCOUNT_RATE_PERCENT = 5

/** はがき郵送費（控除前） */
export function calcPostcardGrossAmount(quantity: number): number {
  return POSTCARD_UNIT_PRICE * quantity
}

/** はがき郵送費の控除額 */
export function calcPostcardDiscountAmount(quantity: number): number {
  return Math.round((POSTCARD_UNIT_PRICE * POSTCARD_DISCOUNT_RATE_PERCENT) / 100 * quantity)
}

/** はがき1枚あたりの控除単価 */
export function calcPostcardDiscountUnitPrice(): number {
  return -Math.round((POSTCARD_UNIT_PRICE * POSTCARD_DISCOUNT_RATE_PERCENT) / 100)
}

/** はがき郵送費の合計を計算: 220 × (100 - 5) / 100 × 枚数 */
export function calcPostcardTotalAmount(quantity: number): number {
  return calcPostcardGrossAmount(quantity) - calcPostcardDiscountAmount(quantity)
}

export function buildPostcardInvoiceItems(
  quantity: number,
  sendRequestNumber?: string | null,
): Array<
  Pick<
    InvoiceItemDoc,
    'item_type' | 'name' | 'amount' | 'unit_price' | 'quantity' | 'total_amount' | 'send_request_number'
  >
> {
  return [
    {
      item_type: 'postcard_postage_fee',
      name: POSTCARD_ITEM_NAME,
      unit_price: POSTCARD_UNIT_PRICE,
      quantity,
      total_amount: calcPostcardGrossAmount(quantity),
      send_request_number: sendRequestNumber ?? null,
    },
    {
      item_type: 'postcard_postage_discount',
      name: POSTCARD_DISCOUNT_ITEM_NAME,
      unit_price: calcPostcardDiscountUnitPrice(),
      quantity,
      total_amount: -calcPostcardDiscountAmount(quantity),
      send_request_number: sendRequestNumber ?? null,
    },
  ]
}
